import React, { useState } from "react";
import { useI18n } from "../../i18n";
import api from "../../api";

const STATUS_COLOR = {
  green: "#34C759",
  yellow: "#F5C518",
  orange: "#FF8A00",
  red: "#FF3B30",
};

const Checkin = ({ profile }) => {
  const { t, lang } = useI18n();
  const [state, setState] = useState({
    energy: 7, sleep: 7, stress: 4, muscle_soreness: 4, sweat_level: 5,
    cramps: false, dizziness: false, poor_digestion: false,
    unexpected_weight_loss: false, amenorrhea: false,
    day_type: "hard_training",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setState((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/checkin", state, { params: { profile_sex: profile.sex } });
      setResult(r.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="sne-card">
        <h3 className="font-heading text-2xl mb-5">{t.checkin.title}</h3>
        <Slider label={t.checkin.energy} value={state.energy} onChange={(v) => set("energy", v)} testid="ci-energy" />
        <Slider label={t.checkin.sleep} value={state.sleep} onChange={(v) => set("sleep", v)} testid="ci-sleep" />
        <Slider label={t.checkin.stress} value={state.stress} onChange={(v) => set("stress", v)} testid="ci-stress" />
        <Slider label={t.checkin.soreness} value={state.muscle_soreness} onChange={(v) => set("muscle_soreness", v)} testid="ci-soreness" />
        <Slider label={t.checkin.sweat} value={state.sweat_level} onChange={(v) => set("sweat_level", v)} testid="ci-sweat" help={t.checkin.sweat_help} />

        <div className="mt-6 mb-3 font-mono text-xs tracking-widest text-white/50">{t.checkin.symptoms}</div>
        <div className="grid grid-cols-2 gap-2">
          {[
            ["cramps", t.checkin.cramps],
            ["dizziness", t.checkin.dizziness],
            ["poor_digestion", t.checkin.poor_digestion],
            ["unexpected_weight_loss", t.checkin.weight_loss],
          ].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={state[k]} onChange={(e) => set(k, e.target.checked)}
                     data-testid={`ci-${k}`}
                     className="accent-[#FF3B30]" />
              <span className="font-mono text-xs text-white/80">{label}</span>
            </label>
          ))}
          {profile.sex === "F" && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={state.amenorrhea} onChange={(e) => set("amenorrhea", e.target.checked)}
                     data-testid="ci-amenorrhea"
                     className="accent-[#FF3B30]" />
              <span className="font-mono text-xs text-white/80">{t.checkin.amenorrhea}</span>
            </label>
          )}
        </div>

        <div className="mt-6">
          <label className="sne-label">{t.checkin.day_type}</label>
          <select className="sne-select" value={state.day_type} onChange={(e) => set("day_type", e.target.value)}
                  data-testid="ci-day-type">
            <option value="hard_training">{t.plan.day_types.hard_training}</option>
            <option value="light_training">{t.plan.day_types.light_training}</option>
            <option value="rest">{t.plan.day_types.rest}</option>
            <option value="match">{t.plan.day_types.match}</option>
            <option value="double_session">{t.plan.day_types.double_session}</option>
          </select>
        </div>

        <button data-testid="ci-submit" onClick={submit} disabled={loading} className="btn-primary w-full mt-6">
          {loading ? t.common.loading : t.checkin.submit}
        </button>
      </div>

      <div>
        {result ? (
          <div className="sne-card animate-fade-up">
            <div className="font-mono text-xs tracking-widest text-white/50 mb-2">STATUS</div>
            <div className="stat-num text-4xl" data-testid="ci-status" style={{ color: STATUS_COLOR[result.status] }}>
              {t.checkin[`status_${result.status}`]}
            </div>

            {result.medical_alerts?.length > 0 && (
              <div className="mt-5 p-4" style={{ background: "#2A0F0F", border: "2px solid #FF3B30" }}>
                <div className="font-mono text-xs tracking-widest text-[#FF3B30] mb-2">⚠ {t.checkin.medical_alert}</div>
                {result.medical_alerts.map((a, i) => (
                  <div key={i} className="text-sm text-white" data-testid={`ci-alert-${i}`}>{a[`msg_${lang}`]}</div>
                ))}
              </div>
            )}

            {result.adaptations?.length > 0 && (
              <div className="mt-5">
                <div className="font-mono text-xs tracking-widest text-white/50 mb-3">{t.checkin.adaptations}</div>
                {result.adaptations.map((a, i) => (
                  <div key={i} className="mb-3 p-3 border border-[#27272A]">
                    {(a[`adaptations_${lang}`] || a.adaptations_en || []).map((adp, j) => (
                      <div key={j} className="text-sm text-white/80 mb-1">→ {adp}</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="sne-card text-white/40 font-mono text-xs">
            // {lang === "ro" ? "Trimite check-in pentru a vedea adaptările" : "Submit check-in to see adaptations"}
          </div>
        )}
      </div>
    </div>
  );
};

const Slider = ({ label, value, onChange, testid, help }) => (
  <div className="mb-4">
    <div className="flex items-center justify-between mb-2">
      <span className="font-mono text-xs tracking-widest text-white/70">{label}</span>
      <span className="font-mono text-sm" data-testid={`${testid}-value`}>{value}/10</span>
    </div>
    <input type="range" min="1" max="10" value={value} onChange={(e) => onChange(parseInt(e.target.value))}
           className="sne-slider" data-testid={testid} />
    {help && <div className="mt-1 font-mono text-[10px] text-white/40">{help}</div>}
  </div>
);

export default Checkin;
